import { Controller } from "stimulus";

/**
 * Handles refreshing parts of the gravity form from the server.
 */
export default class extends Controller {
    static targets = [
    "form",
    "classTimesField",
    "classField",
    "subClassField",
    "preloader"
    ];
    url = "/wp-json/v1/forms/short-term-application";
    modalOpenedSubscriptionToken = null;
    modalClosedSubscriptionToken = null;
    loadingClass = "short-term-application-form--loading";
    formId = null;

  /**
   * Get the current form data object
   */
    get formData()
    {
        if (!this.hasFormTarget) {
            return;
        }
        return new FormData(this.formTarget);
    }

  /**
   * The form is mounted
   */
    connect()
    {
        this.modalOpenedSubscriptionToken = PubSub.subscribe(
            "short-term-application-modal:opened",
            this.handleModalOpened.bind(this)
        );

        this.modalOpenedSubscriptionToken = PubSub.subscribe(
            "short-term-application-modal:closed",
            this.handleModalClosed.bind(this)
        );
    }

  /**
   * THe form is unmounted
   */
    disconnect()
    {
        PubSub.unsubscribe(this.modalOpenedSubscriptionToken);
        PubSub.unsubscribe(this.modalClosedSubscriptionToken);
    }

  /**
   * Handle the modal opened event
   * @param {*} event
   * @param {*} params
   * @returns
   */
    handleModalOpened(event, params = {})
    {
        const classTimes = params.classTimes || [];

        this.startPreloader();
        this.refresh({
            class: params.class,
            subClass: params.subClass,
            classTimes: classTimes.join(","),
        });
    }

    handleModalClosed() {
        //this.refresh();
    }

  /**
   * Handle a change event on a field that has a side effect.
   * Not every field fires this event.
   * @param {*} e
   */
    handleChange(e)
    {
        e.preventDefault();
        this.refresh();
    }

  /**
   * Like handleChange, except we want to clear the class times field.
   */
    handleSubClassChange(e)
    {
        e.preventDefault();
        this.classTimesFieldTarget.innerHTML = "";
        this.refresh();
    }

  /**
   * Like handleChange, except we want to clear our other fields.
   */
    handleClassChange(e)
    {
        e.preventDefault();
        this.subClassFieldTarget.innerHTML = "";
        this.classTimesFieldTarget.innerHTML = "";
        this.refresh();
    }

  /**
   * Inject the preloader
   */
    startPreloader()
    {
        if (!this.hasFormTarget || !this.hasClassTimesFieldTarget) {
            return;
        }
        this.formTarget.classList.add(this.loadingClass);
        this.classTimesFieldTarget.innerHTML = `
        <div class="loader__wrapper">
            <div class="loader">
                <div></div>
                <div></div>
                <div></div>
            </div>
        </div>
        `;
    }

  /**
   * Remove the preloader
   */
    stopPreloader()
    {
        if (this.element.querySelector(".loader__wrapper")) {
            this.element.querySelector(".loader__wrapper").remove();
        }
        if (!this.hasFormTarget) {
            return;
        }
        this.formTarget.classList.remove(this.loadingClass);
    }

  /**
   * Get a fresh instance of the form from the server with our
   * Current form data. This replaces dynamic parts of the form
   * with fresh HTML.
   */
    async refresh(params = {})
    {
      //Do we have the right HTML?
        if (!this.hasClassFieldTarget
        || !this.hasSubClassFieldTarget
        || !this.hasClassTimesFieldTarget
        || !this.hasFormTarget) {
            return;
        }

        const formData = this.formData;

        const formParams = formData ? {
            class: formData.get("input_7"),
            subClass: formData.get("input_18"),
            classTimes: formData.get("input_17"),
        } : {
            class: "",
            subClass: "",
            classTimes: ""
        };

        const payload = Object.assign(formParams, params);

        try {
            const { html } = await this.fetch(payload);

            if (this.hasClassFieldTarget && this.hasSubClassFieldTarget && this.hasClassTimesFieldTarget) {
                this.hydrateTarget(this.classFieldTarget, html);
                this.hydrateTarget(this.subClassFieldTarget, html);
                this.hydrateTarget(this.classTimesFieldTarget, html);
            } else {
                this.hydrateTarget(this.element, html)
            }
        } catch (e) {
            console.log(e);
        }
    }

  /**
   * Replace the HTML of a target element with fresh HTML
   * Takes the full form HTML and extracts what it needs.
   * @param {*} target
   * @param {*} html
   */
    hydrateTarget(target, html)
    {
        const dummy = document.createElement("div");
        dummy.innerHTML = html;

        let replace = dummy.querySelector(
            `[data-target='${target.dataset.target}']`
        );

        if (!replace) {
            replace = dummy.querySelector(
                `[data-controller="short-term-application-form"]`
            )
        }

        target.innerHTML = replace.innerHTML;
    }

  /**
   * Fetch the GF Form and HTML
   * @returns {Promise<{html: string}>}
   */
    async fetch(payload)
    {
        if (!this.shouldFetch(payload)) {
            return Promise.reject("Not needed.");
        }

        this.startPreloader();
        let { classTimes = [], ...params } = payload;

        const query = new URLSearchParams({
            ...params,
        });

        if (classTimes && Array.isArray(classTimes)) {
          //Loop through class times and add them to the search params
            classTimes.forEach((classTime, index) => {
                query.append(`classTimes[${index}]`, classTime);
            });
        } else if (classTimes) {
            query.append("classTimes", classTimes);
        }

        const url = this.url + "?" + query.toString();

        let result;

        try {
            const response = await fetch(url);
            result = await response.json();
            this.stopPreloader();
            return result;
        } catch (e) {
            this.stopPreloader();
            return Promise.reject(e);
        }
    }

  /**
   * Is the data in a place where we're ready to fetch
   * new HTML from the server?
   * @param {*} payload
   * @returns
   */
    shouldFetch(payload)
    {
        if (!payload.class) {
            return false;
        }

        if (!payload.class.includes("|")) {
            return false;
        }

        return true;
    }
}
